@if (visible()) {
  <div class="modal-backdrop" (click)="onCancel()">
    <div class="modal-container" (click)="$event.stopPropagation()">
      <div class="drawer-resizer" appDrawerResize="form-selector-drawer-width"></div>
      <div class="modal-content">
        <div class="search-section">
          <div class="search-row">
            <div class="form-type-filter">
              <label for="formTypeSelect" class="filter-label">Type:</label>
              <select
                id="formTypeSelect"
                class="form-type-select"
                [ngModel]="selectedFormType"
                (ngModelChange)="onFormTypeChange($event)">
                @for (option of formTypeOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
            </div>
            <input
              type="text"
              class="search-input"
              [(ngModel)]="searchText"
              (input)="onSearchChange()"
              placeholder="Search templates by name, display name, or ID..."
              aria-label="Search application templates">
            <button type="button" class="modal-close" (click)="onCancel()" aria-label="Close saved forms" title="Close">
              <i class="pi pi-times"></i>
            </button>
          </div>
          <div class="filter-chips">
            <div class="filter-chip-group">
            <span class="filter-label">Status:</span>
            @for (option of statusOptions; track option.value) {
              <button
                type="button"
                class="filter-chip"
                [class.active]="selectedStatus === option.value"
                (click)="onStatusChipClick(option.value)">
                {{ option.label }}
              </button>
            }
            </div>
            <span class="filter-count" [class.filtered]="filteredTemplates.length !== templates.length">
              <i class="pi pi-filter"></i>
              {{ filteredTemplates.length }} of {{ templates.length }}
              {{ templates.length === 1 ? 'form' : 'forms' }}
            </span>
          </div>
        </div>

        <div class="selector-body">
        <div class="forms-list-container">
          @if (loading$ | async) {
            <div class="loading-section">
              <div class="loading-spinner"></div>
              <p>Loading forms...</p>
            </div>
          }

          @if ((error$ | async) && !(loading$ | async)) {
            <div class="error-section">
              <p class="error-message">{{ error$ | async }}</p>
            </div>
          }

          @if (!(loading$ | async) && !(error$ | async)) {
            <div class="forms-list">
              @for (template of filteredTemplates; track template.id) {
                <div
                  class="form-item"
                  [class.selected]="selectedTemplate?.id === template.id"
                  (click)="selectForm(template)">
                  <div class="form-item-content">
                    <div class="form-item-header">
                      <!-- displayName is "{name} v{version}"; the version gets its own row instead. -->
                      <h3 class="form-item-title">{{ template.name || template.displayName || 'Untitled Form' }}</h3>
                      <div class="form-item-version-row">
                        <span class="form-item-version">Version {{ template.version }}</span>
                        <!-- Only templates made by cloning have anything to say here. -->
                        @if (template.clonedFrom; as clonedFrom) {
                          <span
                            class="clone-info"
                            tabindex="0"
                            role="note"
                            [attr.aria-label]="cloneSummary(clonedFrom)"
                            (click)="$event.stopPropagation()">
                            <i class="pi pi-info-circle" aria-hidden="true"></i>
                            <span class="clone-bubble" role="tooltip">{{ cloneSummary(clonedFrom) }}</span>
                          </span>
                        }
                      </div>
                    </div>
                    <div class="form-item-details">
                      <div class="form-item-row">
                        <span class="form-item-label">Status:</span>
                        <span class="form-item-status"
                          [class.draft]="template.status === 'Draft'"
                          [class.published]="template.status === 'Published'"
                          [class.archived]="template.status === 'Archived'">
                          {{ template.status }}
                        </span>
                      </div>
                      <div class="form-item-row">
                        <span class="form-item-label">ID:</span>
                        <span class="form-item-value form-item-id">{{ template.id }}</span>
                        <app-copy-button [value]="template.id" label="ID" />
                      </div>
                      @if (template.description) {
                        <div class="form-item-row">
                          <span class="form-item-label">Description:</span>
                          <span class="form-item-value form-item-description">{{ template.description }}</span>
                        </div>
                      }
                      <div class="form-item-row">
                        <span class="form-item-label">Questions:</span>
                        <span class="form-item-value">{{ template.totalQuestions || 0 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              }

              @if (filteredTemplates.length === 0 && !(loading$ | async)) {
                <div class="empty-state">
                  <p>No application templates found matching your search.</p>
                </div>
              }
            </div>
          }
        </div>

        <section class="form-preview" aria-label="Form preview" aria-live="polite">
          @switch (preview().kind) {
            @case ('loading') {
              <div class="preview-state">
                <div class="loading-spinner"></div>
                <p>Loading form…</p>
              </div>
            }
            @case ('error') {
              <div class="preview-state">
                <i class="pi pi-exclamation-triangle preview-state-icon"></i>
                <p>{{ previewError() }}</p>
                <button type="button" class="preview-retry" (click)="onRetryPreview()">Try again</button>
              </div>
            }
            @case ('loaded') {
              @if (loadedPreview(); as loaded) {
                <header class="preview-header">
                  <div class="preview-title-row">
                    <h3 class="preview-title">{{ selectedTemplate?.displayName || loaded.form.title || 'Untitled Form' }}</h3>
                    @if (selectedTemplate; as template) {
                      <span class="form-item-status"
                        [class.draft]="template.status === 'Draft'"
                        [class.published]="template.status === 'Published'"
                        [class.archived]="template.status === 'Archived'">
                        {{ template.status }}
                      </span>
                    }
                  </div>
                  @if (loaded.form.description) {
                    <p class="preview-description">{{ loaded.form.description }}</p>
                  }
                  @if (previewCounts(); as counts) {
                    <div class="preview-pills">
                      <span class="preview-pill">Version {{ loaded.form.version ?? 0 }}</span>
                      <span class="preview-pill">
                        {{ counts.sections }} {{ counts.sections === 1 ? 'section' : 'sections' }}
                      </span>
                      <span class="preview-pill">
                        {{ counts.questions }} {{ counts.questions === 1 ? 'question' : 'questions' }}
                      </span>
                      <span class="preview-pill">{{ counts.required }} required</span>
                    </div>
                  }
                </header>
                @if (loaded.questions.length > 0) {
                  <app-preview-sections
                    class="preview-sections"
                    [questions]="loaded.questions"
                    [showQuestionNumbers]="loaded.form.showQuestionNumbers" />
                } @else {
                  <div class="preview-state">
                    <p>This form has no questions yet.</p>
                  </div>
                }
              }
            }
            @default {
              <div class="preview-state">
                <i class="pi pi-file preview-state-icon"></i>
                <p>Select a form to preview it here.</p>
              </div>
            }
          }
        </section>
        </div>
      </div>

      @if (selectedTemplate) {
        <div class="modal-footer">
          @if (mode() === 'compare') {
            <button
              type="button"
              class="modal-button edit-button"
              (click)="onCompareForm()">
              <i class="pi pi-arrow-right-arrow-left"></i>
              <span>Compare</span>
            </button>
          } @else {
            <button
              type="button"
              class="modal-button edit-button"
              (click)="onEditForm()"
              [disabled]="!selectedTemplate">
              <i class="pi pi-pencil"></i>
              <span>Edit Form</span>
            </button>
            <button
              type="button"
              class="modal-button clone-button"
              (click)="onCloneForm()"
              [disabled]="!selectedTemplate">
              <i class="pi pi-copy"></i>
              <span>Clone Form</span>
            </button>
          }
        </div>
      }
    </div>
  </div>
}


